import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Select,
  MultiSelect,
  Paper,
  Badge,
  ScrollArea,
  Tabs,
  Loader,
  Alert,
  Code,
  Table,
  Box,
  Divider,
  TextInput,
} from '@mantine/core';
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import Layout from '../components/Layout';
import api from '../services/api';

interface PreviewModel {
  key: string;
  provider: string;
  modelId: string;
  label: string;
  available: boolean;
  reason?: string;
}

interface PreviewItem {
  id?: string;
  name: string;
  description?: string;
  price?: string | number;
  category?: string;
  props?: Record<string, unknown>;
}

interface ModelResult {
  model: { key: string; label: string; provider: string };
  durationMs: number;
  extractedCount?: number;
  items: PreviewItem[];
  error?: string;
}

type ResultsMap = Record<string, ModelResult>;

const SOURCE_LANGUAGES = [
  { value: 'jp', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
  { value: 'en', label: 'English' },
];

const UPLOAD_MODES = [
  { value: 'translation', label: 'Translation (full pipeline)' },
  { value: 'fast', label: 'Fast (translation only)' },
  { value: 'v2', label: 'v2 (translation + ingredients)' },
];

const TestUpload: React.FC = () => {
  const [file, setFile] = useState<FileWithPath | null>(null);
  const [availableModels, setAvailableModels] = useState<PreviewModel[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>(['gemini-2.5-flash']);
  const [sourceLanguage, setSourceLanguage] = useState('jp');
  const [uploadMode, setUploadMode] = useState('translation');
  const [restaurantName, setRestaurantName] = useState('Preview Restaurant');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ResultsMap | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);

  useEffect(() => {
    api.get('/preview/models')
      .then(res => {
        setAvailableModels(res.data.models);
      })
      .catch(err => {
        console.error('Failed to fetch available models:', err);
      });
  }, []);

  const handleRun = async () => {
    if (!file) {
      notifications.show({ message: 'Please select an image first', color: 'red' });
      return;
    }
    if (selectedModels.length === 0) {
      notifications.show({ message: 'Select at least one model', color: 'red' });
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('menuImage', file);
      formData.append('models', JSON.stringify(selectedModels));
      formData.append('sourceLanguage', sourceLanguage);
      formData.append('uploadMode', uploadMode);
      formData.append('restaurantName', restaurantName);

      const res = await api.post('/preview/run', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min — full pipeline can take a while
      });

      setResults(res.data.results);
      const firstKey = Object.keys(res.data.results)[0];
      if (firstKey) setActiveTab(firstKey);

      notifications.show({ message: 'Preview complete', color: 'green' });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } }; message?: string })
        ?.response?.data?.error ?? (err as { message?: string })?.message ?? 'Unknown error';
      notifications.show({ message: `Error: ${message}`, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const modelSelectData = availableModels.map(m => ({
    value: m.key,
    label: `${m.label}${m.available ? '' : ' (no API key)'}`,
    disabled: !m.available,
  }));

  const imagePreviewUrl = file ? URL.createObjectURL(file) : null;

  return (
    <Layout>
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <div>
            <Title order={2}>Test Upload — Model Preview</Title>
            <Text c="dimmed" size="sm">
              Run the full AI pipeline against multiple models without saving to the database.
            </Text>
          </div>

          <Paper withBorder p="md" radius="md">
            <Stack gap="md">
              <Title order={4}>Configuration</Title>

              <Group align="flex-start" grow>
                <Select
                  label="Source language"
                  data={SOURCE_LANGUAGES}
                  value={sourceLanguage}
                  onChange={(v) => setSourceLanguage(v ?? 'jp')}
                />
                <Select
                  label="Upload mode"
                  data={UPLOAD_MODES}
                  value={uploadMode}
                  onChange={(v) => setUploadMode(v ?? 'translation')}
                />
                <TextInput
                  label="Restaurant name (for AI context)"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.currentTarget.value)}
                />
              </Group>

              <MultiSelect
                label="Models to compare"
                data={modelSelectData}
                value={selectedModels}
                onChange={setSelectedModels}
                placeholder="Select one or more models"
                description="Models without an API key configured are disabled"
              />

              <Divider />

              <Dropzone
                onDrop={(files) => setFile(files[0])}
                accept={IMAGE_MIME_TYPE}
                maxFiles={1}
                multiple={false}
              >
                <Stack align="center" gap="xs" py="md">
                  <Text size="sm" c="dimmed">Drop a menu image here or click to browse</Text>
                  {file && <Badge color="green">{file.name}</Badge>}
                </Stack>
              </Dropzone>

              {imagePreviewUrl && (
                <Box>
                  <img
                    src={imagePreviewUrl}
                    alt="Menu preview"
                    style={{ maxHeight: 200, maxWidth: '100%', borderRadius: 8 }}
                  />
                </Box>
              )}

              <Button
                onClick={handleRun}
                loading={loading}
                disabled={!file || selectedModels.length === 0}
                size="md"
              >
                {loading ? 'Running pipeline…' : 'Run preview'}
              </Button>
            </Stack>
          </Paper>

          {loading && (
            <Group justify="center" py="xl">
              <Loader size="lg" />
              <Text>Running AI pipeline for {selectedModels.length} model(s)… this may take a few minutes.</Text>
            </Group>
          )}

          {results && (
            <Paper withBorder p="md" radius="md">
              <Stack gap="md">
                <Title order={4}>Results</Title>

                <Group gap="xs">
                  {Object.entries(results).map(([key, r]) => (
                    <Badge
                      key={key}
                      color={r.error ? 'red' : 'green'}
                      variant="light"
                    >
                      {r.model?.label ?? key} — {r.error ? 'error' : `${r.items?.length ?? 0} items`} ({(r.durationMs / 1000).toFixed(1)}s)
                    </Badge>
                  ))}
                </Group>

                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    {Object.entries(results).map(([key, r]) => (
                      <Tabs.Tab key={key} value={key} color={r.error ? 'red' : undefined}>
                        {r.model?.label ?? key}
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>

                  {Object.entries(results).map(([key, r]) => (
                    <Tabs.Panel key={key} value={key} pt="md">
                      {r.error ? (
                        <Alert color="red" title="Error">
                          {r.error}
                        </Alert>
                      ) : (
                        <Stack gap="md">
                          <Group gap="lg">
                            <Text size="sm"><strong>Extracted:</strong> {r.extractedCount ?? r.items?.length} items</Text>
                            <Text size="sm"><strong>Duration:</strong> {(r.durationMs / 1000).toFixed(1)}s</Text>
                            <Text size="sm"><strong>Provider:</strong> {r.model?.provider}</Text>
                          </Group>

                          <ScrollArea h={500}>
                            <Table striped highlightOnHover withTableBorder withColumnBorders>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ minWidth: 140 }}>Name (source)</Table.Th>
                                  <Table.Th style={{ minWidth: 140 }}>Name (EN)</Table.Th>
                                  <Table.Th style={{ minWidth: 100 }}>Category</Table.Th>
                                  <Table.Th style={{ minWidth: 80 }}>Price</Table.Th>
                                  <Table.Th style={{ minWidth: 200 }}>Description (EN)</Table.Th>
                                  <Table.Th style={{ minWidth: 160 }}>Top ingredients</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {(r.items ?? []).map((item, i) => {
                                  const enName = (item.props as Record<string, unknown> | undefined)
                                    ?.translations
                                    ? ((item.props as Record<string, unknown>).translations as Record<string, unknown>)
                                        ?.en
                                      ? (((item.props as Record<string, unknown>).translations as Record<string, unknown>).en as Record<string, unknown>)
                                          ?.name
                                        ? ((((item.props as Record<string, unknown>).translations as Record<string, unknown>).en as Record<string, unknown>).name as { text?: string })?.text
                                        : null
                                      : null
                                    : null;
                                  const enDesc = (item.props as Record<string, unknown> | undefined)
                                    ?.translations
                                    ? ((item.props as Record<string, unknown>).translations as Record<string, unknown>)
                                        ?.en
                                      ? (((item.props as Record<string, unknown>).translations as Record<string, unknown>).en as Record<string, unknown>)
                                          ?.description
                                        ? ((((item.props as Record<string, unknown>).translations as Record<string, unknown>).en as Record<string, unknown>).description as { text?: string })?.text
                                        : null
                                      : null
                                    : null;
                                  const ingredients = (item.props as Record<string, unknown> | undefined)
                                    ?.analysis
                                    ? (((item.props as Record<string, unknown>).analysis as Record<string, unknown>)?.ingredients as Array<{ name_en?: string; canonical_name?: string }> | undefined)
                                    : null;
                                  const topIngredients = ingredients
                                    ?.slice(0, 3)
                                    .map(ing => ing.name_en || ing.canonical_name)
                                    .filter(Boolean)
                                    .join(', ');
                                  return (
                                    <Table.Tr key={i}>
                                      <Table.Td>{item.name}</Table.Td>
                                      <Table.Td>{enName ?? <Text c="dimmed" size="xs">—</Text>}</Table.Td>
                                      <Table.Td>{item.category}</Table.Td>
                                      <Table.Td>{item.price != null ? String(item.price) : '—'}</Table.Td>
                                      <Table.Td>
                                        <Text size="xs" lineClamp={3}>{enDesc ?? '—'}</Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="xs">{topIngredients || '—'}</Text>
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>
                          </ScrollArea>

                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: 13, color: '#888' }}>Raw JSON</summary>
                            <ScrollArea h={300}>
                              <Code block style={{ fontSize: 11 }}>
                                {JSON.stringify(r.items, null, 2)}
                              </Code>
                            </ScrollArea>
                          </details>
                        </Stack>
                      )}
                    </Tabs.Panel>
                  ))}
                </Tabs>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </Layout>
  );
};

export default TestUpload;
